/**
 * useBusinessUnits — hook for business unit API operations.
 */

import { useState, useEffect, useCallback } from "react";
import { api, getErrorMessage } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import type {
  BusinessUnit,
  BusinessUnitAppliance,
  ApplianceElementCandidate,
  ApplianceStatus,
} from "../models/types";

interface BusinessUnitsResponse {
  businessUnits: BusinessUnit[];
  count: number;
}

interface BusinessUnitResponse {
  businessUnit: BusinessUnit;
}

interface CreateBusinessUnitPayload {
  name: string;
  description?: string;
  color?: string;
}

interface UpdateBusinessUnitPayload {
  name?: string;
  description?: string;
  color?: string;
}

export function useBusinessUnits() {
  const { isAuthenticated } = useAuth();
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBusinessUnits = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<BusinessUnitsResponse>("/api/business-units");
      setBusinessUnits(data.businessUnits);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load business units"));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchBusinessUnits();
  }, [fetchBusinessUnits]);

  return { businessUnits, loading, error, refetch: fetchBusinessUnits };
}

export function useCreateBusinessUnit() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createBusinessUnit = async (payload: CreateBusinessUnitPayload): Promise<BusinessUnit | null> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.post<BusinessUnitResponse>("/api/business-units", { body: payload });
      return data.businessUnit;
    } catch (err) {
      setError(getErrorMessage(err, "Failed to create business unit"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { createBusinessUnit, loading, error, clearError: () => setError(null) };
}

export function useUpdateBusinessUnit() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateBusinessUnit = async (id: string, payload: UpdateBusinessUnitPayload): Promise<BusinessUnit | null> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.put<BusinessUnitResponse>(`/api/business-units/${id}`, { body: payload });
      return data.businessUnit;
    } catch (err) {
      setError(getErrorMessage(err, "Failed to update business unit"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { updateBusinessUnit, loading, error, clearError: () => setError(null) };
}

export function useDeleteBusinessUnit() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteBusinessUnit = async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/api/business-units/${id}`);
      return true;
    } catch (err) {
      setError(getErrorMessage(err, "Failed to delete business unit"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { deleteBusinessUnit, loading, error, clearError: () => setError(null) };
}

// ============================================================================
// MediaPlex appliance operations
// ============================================================================

export interface SetApplianceInput {
  mode?: "direct" | "iot";
  label?: string;
  /** Optional client scope. On update: "" clears the scope, undefined keeps it. */
  clientId?: string;
  baseUrl?: string;
  /** Reference to an existing Secrets Manager secret holding the mpk_ key. */
  apiKeySecretRef?: string;
  /** Raw mpk_ key pasted by an admin; backend provisions the secret from it. */
  apiKey?: string;
  applianceId?: string;
}

interface ApplianceMutationResponse {
  businessUnit: BusinessUnit;
  appliance?: BusinessUnitAppliance;
}

interface ApplianceElementsResponse {
  candidates: ApplianceElementCandidate[];
  count: number;
  streamCount: number;
  appliances: ApplianceStatus[];
}

/**
 * Mutate the MediaPlex appliance list on a Business Unit: add a new appliance,
 * update an existing entry (by id), or remove one (by id). A BU may hold several
 * appliances, each optionally scoped to a single client.
 */
export function useBUAppliances() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addAppliance = async (
    id: string,
    payload: SetApplianceInput,
  ): Promise<BusinessUnit | null> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.post<ApplianceMutationResponse>(
        `/api/business-units/${id}/appliances`,
        { body: payload },
      );
      return data.businessUnit;
    } catch (err) {
      setError(getErrorMessage(err, "Failed to add appliance"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateAppliance = async (
    id: string,
    applianceId: string,
    payload: SetApplianceInput,
  ): Promise<BusinessUnit | null> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.put<ApplianceMutationResponse>(
        `/api/business-units/${id}/appliances/${applianceId}`,
        { body: payload },
      );
      return data.businessUnit;
    } catch (err) {
      setError(getErrorMessage(err, "Failed to update appliance"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const removeAppliance = async (
    id: string,
    applianceId: string,
  ): Promise<BusinessUnit | null> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.delete<ApplianceMutationResponse>(
        `/api/business-units/${id}/appliances/${applianceId}`,
      );
      return data.businessUnit;
    } catch (err) {
      setError(getErrorMessage(err, "Failed to remove appliance"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    addAppliance,
    updateAppliance,
    removeAppliance,
    loading,
    error,
    clearError: () => setError(null),
  };
}

/**
 * Fetch the LIVE element candidates across all appliances assigned to a BU.
 * Live-linked: call this each time the picker opens/refreshes (never snapshot).
 * Also serves as the "connection test" for the assign UI. Pass a clientId to
 * limit to appliances serving that client (scoped + BU-wide).
 */
export function useApplianceElements() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchElements = async (
    id: string,
    clientId?: string,
  ): Promise<ApplianceElementsResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const qs = clientId ? `?clientId=${encodeURIComponent(clientId)}` : "";
      return await api.get<ApplianceElementsResponse>(
        `/api/business-units/${id}/appliance/elements${qs}`,
      );
    } catch (err) {
      setError(getErrorMessage(err, "Failed to reach appliance"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { fetchElements, loading, error, clearError: () => setError(null) };
}

/** A registered appliance from the NEP-only device-id directory. */
export interface RegisteredAppliance {
  applianceId: string;
  /** Appliance public base URL, when reported — used as a friendly picker hint. */
  baseUrl?: string;
  lastSeenAt?: string;
  streamsReportedAt?: string;
  hasStreams: boolean;
}

interface RegisteredAppliancesResponse {
  appliances: RegisteredAppliance[];
  count: number;
}

/**
 * NEP-only directory of appliances that have reported to the IoT control plane,
 * backing the "Appliance device ID" picker for IoT (Mode A) assignments. The
 * endpoint is restricted to @nepgroup.com admins server-side; for a non-NEP
 * caller it returns 403, which this hook surfaces as an empty list (the UI does
 * not offer the picker to non-NEP admins anyway). Call `fetch()` lazily when the
 * IoT device-id field is shown.
 */
export function useRegisteredAppliances() {
  const [appliances, setAppliances] = useState<RegisteredAppliance[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRegistered = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<RegisteredAppliancesResponse>(
        `/api/business-units/appliances/registered`,
      );
      setAppliances(data.appliances ?? []);
      setLoaded(true);
    } catch (err) {
      // 403 (non-NEP) or transient failure: leave the list empty; the field
      // still accepts manual entry.
      setError(getErrorMessage(err, "Failed to load registered appliances"));
      setAppliances([]);
    } finally {
      setLoading(false);
    }
  };

  return { appliances, fetchRegistered, loading, loaded, error };
}

export type { BusinessUnitAppliance, ApplianceElementCandidate, ApplianceStatus };
